import React, { useState, useEffect } from 'react';
import { Activity, Users, Clock, Calendar, BarChart2, GitBranch, AlertCircle, Zap } from 'lucide-react';

const BehavioralAnalytics: React.FC = () => {
  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('today');
  const [activeAlerts, setActiveAlerts] = useState<any[]>([]);
  const [monitoredZones, setMonitoredZones] = useState<{ [key: string]: boolean }>({
    'Open Office': true,
    'Meeting Areas': true,
    'Break Rooms': true,
    'Quiet Zones': true
  });

  // Mock data
  const analyticsData = {
    overview: {
      dailyOccupancy: 342,
      peakTime: '10:30 AM',
      avgStayDuration: '6.5 hours',
      collaborationScore: 78
    },
    spaceUsage: {
      standingDesks: { count: 45, utilization: 82 },
      sittingDesks: { count: 125, utilization: 65 },
      collaborationAreas: { count: 85, utilization: 78 },
      quietZones: { count: 35, utilization: 88 }
    },
    departmentInteractions: [
      {
        departments: ['Engineering', 'Product'],
        frequency: 85,
        locations: ['Innovation Hub', 'Break Room'],
        duration: '45 mins avg'
      },
      {
        departments: ['Sales', 'Marketing'],
        frequency: 72,
        locations: ['Meeting Room A', 'Open Space'],
        duration: '30 mins avg'
      },
      {
        departments: ['Design', 'Engineering'],
        frequency: 68,
        locations: ['Prototype Lab', 'Collaboration Zone'],
        duration: '60 mins avg'
      }
    ],
    timeAnalysis: {
      individual: [
        { hour: '8AM', usage: 45 },
        { hour: '9AM', usage: 75 },
        { hour: '10AM', usage: 85 },
        { hour: '11AM', usage: 80 },
        { hour: '12PM', usage: 60 },
        { hour: '1PM', usage: 65 },
        { hour: '2PM', usage: 70 },
        { hour: '3PM', usage: 75 },
        { hour: '4PM', usage: 65 },
        { hour: '5PM', usage: 45 }
      ],
      collaborative: [
        { hour: '8AM', usage: 20 },
        { hour: '9AM', usage: 45 },
        { hour: '10AM', usage: 65 },
        { hour: '11AM', usage: 70 },
        { hour: '12PM', usage: 40 },
        { hour: '1PM', usage: 35 },
        { hour: '2PM', usage: 55 },
        { hour: '3PM', usage: 60 },
        { hour: '4PM', usage: 50 },
        { hour: '5PM', usage: 30 }
      ]
    },
    patterns: [
      {
        type: 'Collaboration Hotspot',
        location: 'Innovation Hub',
        peakTime: '10:00 AM - 12:00 PM',
        participants: 12,
        frequency: 'Daily'
      },
      {
        type: 'Quiet Zone Usage',
        location: 'Focus Pods',
        peakTime: '2:00 PM - 4:00 PM',
        participants: 8,
        frequency: 'Daily'
      },
      {
        type: 'Social Interaction',
        location: 'Break Room',
        peakTime: '12:00 PM - 1:00 PM',
        participants: 25,
        frequency: 'Daily'
      }
    ],
    distancingAlerts: [
      {
        zone: 'Open Office North',
        currentDensity: 85,
        recommendedMax: 75,
        status: 'warning'
      },
      {
        zone: 'Meeting Room A',
        currentDensity: 95,
        recommendedMax: 80,
        status: 'alert'
      },
      {
        zone: 'Break Room',
        currentDensity: 65,
        recommendedMax: 70,
        status: 'optimal'
      }
    ]
  };

  // Simulated real-time updates
  useEffect(() => {
    const interval = setInterval(() => {
      const newAlert = {
        id: Date.now(),
        zone: `Zone ${String.fromCharCode(65 + Math.floor(Math.random() * 4))}`,
        type: Math.random() > 0.5 ? 'Density Warning' : 'Distance Alert',
        timestamp: new Date().toLocaleTimeString(),
        status: Math.random() > 0.5 ? 'warning' : 'alert'
      };
      setActiveAlerts(prev => [...prev.slice(-4), newAlert]);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const handleZoneToggle = (zone: string) => {
    setMonitoredZones(prev => ({
      ...prev,
      [zone]: !prev[zone]
    }));
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'optimal':
        return 'text-green-500';
      case 'warning':
        return 'text-yellow-500';
      case 'alert':
        return 'text-red-500';
      default:
        return 'text-gray-500';
    }
  };

  const getUtilizationColor = (utilization: number) => {
    if (utilization >= 85) return 'bg-red-500';
    if (utilization >= 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">Behavioral Analytics</h2>
      
      {/* Overview Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Users className="text-blue-500 mr-2" />
            <h3 className="text-lg font-semibold">Daily Occupancy</h3>
          </div>
          <p className="text-3xl font-bold">{analyticsData.overview.dailyOccupancy}</p>
          <p className="text-gray-500">People today</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Clock className="text-green-500 mr-2" />
            <h3 className="text-lg font-semibold">Peak Activity</h3>
          </div>
          <p className="text-3xl font-bold">{analyticsData.overview.peakTime}</p>
          <p className="text-gray-500">Busiest time</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Calendar className="text-purple-500 mr-2" />
            <h3 className="text-lg font-semibold">Avg Stay Time</h3>
          </div>
          <p className="text-3xl font-bold">{analyticsData.overview.avgStayDuration}</p>
          <p className="text-gray-500">Per person</p>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <div className="flex items-center mb-2">
            <Activity className="text-orange-500 mr-2" />
            <h3 className="text-lg font-semibold">Collaboration</h3>
          </div>
          <p className="text-3xl font-bold">{analyticsData.overview.collaborationScore}</p>
          <p className="text-gray-500">Interaction score</p>
        </div>
      </div>

      {/* Zone Monitoring Controls */}
      <div className="mb-8 bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-semibold">Zone Monitoring</h3>
          <div className="flex gap-2">
            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="px-3 py-2 border rounded-lg"
            >
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Object.entries(monitoredZones).map(([zone, isActive]) => (
            <div key={zone} className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-4">
                <span className="font-medium">{zone}</span>
                <button
                  onClick={() => handleZoneToggle(zone)}
                  className={`px-4 py-2 rounded-lg font-medium ${
                    isActive
                      ? 'bg-green-500 text-white'
                      : 'bg-gray-200 text-gray-600'
                  }`}
                >
                  {isActive ? 'Monitoring' : 'Paused'}
                </button>
              </div>
              {isActive && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Current Density:</span>
                    <span className="font-medium">65%</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span>Social Distance:</span>
                    <span className="font-medium text-green-500">Optimal</span>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Department Interactions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Department Interactions</h3>
          <div className="space-y-4">
            {analyticsData.departmentInteractions.map((interaction, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{interaction.departments.join(' + ')}</h4>
                    <p className="text-sm text-gray-500">
                      {interaction.locations.join(', ')}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-medium">{interaction.frequency}%</span>
                    <p className="text-sm text-gray-500">{interaction.duration}</p>
                  </div>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-500 rounded-full h-2"
                    style={{ width: `${interaction.frequency}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Social Distancing Alerts</h3>
          <div className="space-y-4">
            {analyticsData.distancingAlerts.map((alert, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <h4 className="font-medium">{alert.zone}</h4>
                    <p className="text-sm text-gray-500">
                      Max Recommended: {alert.recommendedMax}%
                    </p>
                  </div>
                  <span className={`font-medium ${getStatusColor(alert.status)}`}>
                    {alert.currentDensity}%
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className={`rounded-full h-2 ${
                      alert.currentDensity > alert.recommendedMax ? 'bg-red-500' : 'bg-green-500'
                    }`}
                    style={{ width: `${(alert.currentDensity / alert.recommendedMax) * 100}%` }}
                  ></div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Patterns and Real-time Alerts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Behavior Patterns</h3>
          <div className="space-y-4">
            {analyticsData.patterns.map((pattern, index) => (
              <div key={index} className="border-b pb-4 last:border-b-0">
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="font-medium">{pattern.type}</h4>
                    <p className="text-sm text-gray-500">{pattern.location}</p>
                    <p className="text-sm text-gray-500">
                      {pattern.peakTime} • {pattern.frequency}
                    </p>
                  </div>
                  <div className="text-right">
                    <span className="font-medium">{pattern.participants}</span>
                    <p className="text-sm text-gray-500">participants</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-xl font-semibold mb-4">Real-time Alerts</h3>
          <div className="space-y-4">
            {activeAlerts.map((alert) => (
              <div key={alert.id} className="flex items-start justify-between border-b pb-4 last:border-b-0">
                <div>
                  <p className={`font-medium ${getStatusColor(alert.status)}`}>
                    {alert.type}
                  </p>
                  <p className="text-sm text-gray-500">
                    {alert.zone} • {alert.timestamp}
                  </p>
                </div>
                <AlertCircle
                  className={`${
                    alert.status === 'alert' ? 'text-red-500' : 'text-yellow-500'
                  }`}
                />
              </div>
            ))}
            {activeAlerts.length === 0 && (
              <p className="text-gray-500 text-center">No active alerts</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BehavioralAnalytics; 